import { RefObject, useEffect, useRef } from "react";

/**
 * Keep a chat transcript pinned to its newest message.
 *
 * Scrolls the transcript's own container rather than calling `scrollIntoView`
 * on a sentinel at the bottom. `scrollIntoView` walks every scrollable
 * ancestor, so on a page where the transcript sits beside a summary panel it
 * scrolls the *page* too — the reply arrives and the whole form jumps down to
 * the panel instead of staying on the conversation.
 */
export const useScrollToLatest = <T>(
	deps: T[],
): RefObject<HTMLDivElement | null> => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		// jsdom implements neither scroll method, and older browsers lack the
		// options form — assigning scrollTop works everywhere, just without the
		// animation.
		if (typeof container.scrollTo === "function") {
			container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
		} else {
			container.scrollTop = container.scrollHeight;
		}
		// The caller decides what counts as "something new arrived".
	}, deps);

	return containerRef;
};
